import React, { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Brand } from '../dashboard/Brand';
import { Input } from '../ui';
import { StakeholderMap } from './StakeholderMap';

/*
 * Shared chrome for the screens outside the app shell (sign in, register,
 * first-run setup, onboarding). Phones: full-screen white page, safe-area
 * aware, actions anchored to the bottom. From `sm`: centred 380px card on a
 * neutral-50 ground.
 *
 * The front door — sign in and register — passes `aside`, which from `lg`
 * moves the form to a left rail and gives the rest of the screen to the
 * stakeholder map. The first-run and onboarding wizards do not: nothing
 * should compete with a task you are halfway through.
 */

/** What the door says. One line, and one line about it. */
const ASIDE_TITLE = 'Every stakeholder, in context.';
const ASIDE_META =
  'AI-first stakeholder management and intelligence — for companies, agencies and institutions.';

interface AuthScreenProps {
  title?: React.ReactNode;
  /** One short line under the title */
  meta?: React.ReactNode;
  /** Replaces the brand mark row, e.g. brand + skip */
  top?: React.ReactNode;
  /** Sits between the brand row and the title, e.g. a StepBar */
  progress?: React.ReactNode;
  /** Show the stakeholder map: a ribbon on phones, half the screen from `lg` */
  aside?: boolean;
  children: React.ReactNode;
}

export const AuthScreen: React.FC<AuthScreenProps> = ({ title, meta, top, progress, aside = false, children }) => {
  const column = (
    <main
      className={`mx-auto flex min-h-[100dvh] w-full max-w-[380px] flex-col px-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-[calc(env(safe-area-inset-top)_+_3rem)] sm:min-h-0 sm:rounded-2xl sm:border sm:border-neutral-200 sm:bg-white sm:px-7 sm:pb-7 sm:pt-7 ${
        aside ? 'lg:mx-0 lg:min-h-0 lg:rounded-none lg:border-0 lg:px-0 lg:pb-0 lg:pt-0' : ''
      }`}
    >
      {top ?? <Brand />}
      {aside && (
        <div className="-mx-5 mt-5 h-32 overflow-hidden bg-neutral-50 sm:-mx-7 sm:mt-6 lg:hidden">
          <StakeholderMap variant="ribbon" className="h-full w-full" />
        </div>
      )}
      {progress && <div className="mt-6">{progress}</div>}
      {title && (
        <div className={progress || aside ? `mt-5${aside ? ' lg:mt-9' : ''}` : 'mt-8'}>
          <h1 className="text-[24px] font-normal leading-tight tracking-[-0.03em] text-ink">{title}</h1>
          {meta && <p className="mt-1 text-[13px] text-neutral-500">{meta}</p>}
        </div>
      )}
      <div className="mt-6 flex flex-1 flex-col">{children}</div>
    </main>
  );

  const ground =
    'min-h-[100dvh] bg-white font-ui text-ink antialiased sm:flex sm:items-center sm:justify-center sm:bg-neutral-50 sm:px-6 sm:py-12';

  if (!aside) return <div className={ground}>{column}</div>;

  return (
    <div className={`${ground} lg:grid lg:grid-cols-[minmax(0,46%)_1fr] lg:items-stretch lg:bg-white lg:p-0`}>
      <div className="lg:flex lg:items-center lg:justify-center lg:border-r lg:border-neutral-200 lg:px-12 lg:py-10">
        {column}
      </div>
      <aside className="relative hidden overflow-hidden bg-neutral-50 lg:block">
        <StakeholderMap className="absolute left-1/2 top-1/2 aspect-square w-[104%] -translate-x-1/2 -translate-y-[54%]" />
        <div className="absolute inset-x-0 bottom-0 animate-fade-up p-10">
          <p className="max-w-[24ch] text-[19px] font-light leading-tight tracking-[-0.02em] text-ink">{ASIDE_TITLE}</p>
          <p className="mt-2.5 max-w-[46ch] text-[13px] leading-relaxed text-neutral-500">{ASIDE_META}</p>
        </div>
      </aside>
    </div>
  );
};

/** Bottom action group: pinned to the bottom of the screen on phones, follows the fields on larger screens. */
export const AuthActions: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <div className={`mt-auto space-y-3 pt-8 sm:mt-0 sm:pt-6 ${className}`}>{children}</div>
);

/** Full-width primary/secondary button sizing: 40px on phones, 36px from `sm` */
export const AUTH_BUTTON = 'h-10 w-full sm:h-9';

/** Small centred secondary line under the actions */
export const AuthFootnote: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-center text-[13px] text-neutral-500">{children}</p>
);

export const AUTH_LINK =
  'text-ink underline-offset-4 transition-colors hover:text-primary-600 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40 rounded';

/** Inline message row: red for errors, green for success */
export const FormNotice: React.FC<{ tone?: 'error' | 'success'; children: React.ReactNode }> = ({
  tone = 'error',
  children,
}) =>
  tone === 'error' ? (
    <p role="alert" className="animate-fade-up rounded-xl bg-primary-50 px-3.5 py-2.5 text-[13px] text-primary-700">
      {children}
    </p>
  ) : (
    <p role="status" className="animate-fade-up rounded-xl bg-emerald-50 px-3.5 py-2.5 text-[13px] text-emerald-700">
      {children}
    </p>
  );

/** Password field with a show/hide toggle */
export const PasswordInput = React.forwardRef<
  HTMLInputElement,
  Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'>
>(({ className = '', ...props }, ref) => {
  const [visible, setVisible] = useState(false);
  const Icon = visible ? EyeOff : Eye;
  return (
    <div className="relative">
      <Input ref={ref} type={visible ? 'text' : 'password'} className={`pr-11 ${className}`} {...props} />
      <button
        type="button"
        onClick={() => setVisible(v => !v)}
        aria-label={visible ? 'Hide password' : 'Show password'}
        title={visible ? 'Hide password' : 'Show password'}
        className="absolute right-1 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-neutral-400 transition-colors hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40 sm:h-7 sm:w-7"
      >
        <Icon className="h-4 w-4" strokeWidth={1.75} />
      </button>
    </div>
  );
});
PasswordInput.displayName = 'PasswordInput';

/** Thin progress segments */
export const StepBar: React.FC<{ current: number; total: number; className?: string }> = ({
  current,
  total,
  className = '',
}) => (
  <div
    role="progressbar"
    aria-label={`Step ${current + 1} of ${total}`}
    aria-valuemin={1}
    aria-valuemax={total}
    aria-valuenow={current + 1}
    className={`flex gap-1 ${className}`}
  >
    {Array.from({ length: total }, (_, index) => (
      <span
        key={index}
        className={`h-[3px] flex-1 rounded-full transition-colors duration-300 ${
          index <= current ? 'bg-primary-600' : 'bg-neutral-200'
        }`}
      />
    ))}
  </div>
);

/** Splash while auth / setup status loads: the app mark, gently pulsing */
export const AuthSplash: React.FC<{ label?: string }> = ({ label = 'Loading...' }) => (
  <div role="status" className="flex min-h-[100dvh] items-center justify-center bg-white font-ui">
    <Brand markOnly className="animate-pulse" />
    <span className="sr-only">{label}</span>
  </div>
);
